<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

type Props = {
  duration: number
  start: number
  end: number
  seek: number
  mask?: number | null
  minGap?: number
  disabled?: boolean
  noBg?: boolean
}

const props = withDefaults(defineProps<Props>(), { minGap: 3, disabled: false, noBg: false, mask: null })
const emit = defineEmits<{
  (e: 'update:start', v: number): void
  (e: 'update:end', v: number): void
  (e: 'update:seek', v: number): void
  (e: 'preview-trim', v: number): void
  (e: 'preview-seek', v: number): void
  (e: 'seek-dragging', v: boolean): void
}>()

const wrapEl = ref<HTMLElement | null>(null)
const trackEl = ref<HTMLElement | null>(null)
const railEl = ref<HTMLElement | null>(null)

const edgePad = 14

const zoom = ref(1)
const zoomMin = 1
const zoomMax = 6

const dragging = ref<null | 'start' | 'end' | 'range' | 'seek'>(null)
const dragPointerId = ref<number | null>(null)
const dragStartX = ref(0)
const dragStartStart = ref(0)
const dragStartEnd = ref(0)
const dragStartSeek = ref(0)
const dragSeekOffsetFromStart = ref(0)
const dragSeekOffsetFromEnd = ref(0)
const dragRangeOffset = ref(0)

const hover = ref(false)
const tooltipOpen = ref(false)
const tooltipX = ref(0)
const tooltipText = ref('')

const pinchActive = ref(false)
const pinchStartDist = ref(0)
const pinchStartZoom = ref(1)
const pinchPointers = new Map<number, { x: number; y: number }>()

let ro: ResizeObserver | null = null
const wrapW = ref(0)

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v))
}

function clampTime(v: number) {
  const d = Math.max(0, Number(props.duration || 0))
  if (!d) return 0
  if (!Number.isFinite(v)) return 0
  return Math.max(0, Math.min(d, v))
}

function clampSeekToTrim(v: number) {
  const s = startC.value
  const e = endC.value
  return Math.max(s, Math.min(e, clampTime(v)))
}

function roundMs(v: number) {
  return Math.round(v * 1000) / 1000
}

function setStartEnd(nextStart: number, nextEnd: number) {
  const d = Math.max(0, Number(props.duration || 0))
  const gap = Math.max(0, Number(props.minGap || 0))
  let s = clampTime(nextStart)
  let e = clampTime(nextEnd)
  if (d > 0) {
    if (e - s < gap) {
      if (dragging.value === 'start') s = Math.max(0, e - gap)
      else if (dragging.value === 'end') e = Math.min(d, s + gap)
      else e = Math.min(d, s + gap)
    }
  }
  s = roundMs(s)
  e = roundMs(e)
  emit('update:start', s)
  emit('update:end', e)
}

function setSeek(next: number) {
  const t = roundMs(clampSeekToTrim(next))
  emit('update:seek', t)
}

function mmss(v: number) {
  const s = Math.max(0, Number.isFinite(v) ? v : 0)
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  const ms = Math.floor((s - Math.floor(s)) * 1000)
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}.${String(ms).padStart(3, '0')}`
}

const durationOk = computed(() => Math.max(0, Number(props.duration || 0)) > 0)
const startC = computed(() => clampTime(props.start))
const endC = computed(() => {
  const d = Math.max(0, Number(props.duration || 0))
  if (!d) return 0
  const gap = Math.max(0, Number(props.minGap || 0))
  return Math.max(0, Math.min(d, Math.max(props.end, startC.value + gap)))
})

const seekC = computed(() => clampSeekToTrim(props.seek))

const innerW = computed(() => {
  const w = Math.max(0, wrapW.value)
  if (!w) return 0
  return Math.round(w * Math.max(zoomMin, Math.min(zoomMax, zoom.value)))
})

const usableW = computed(() => Math.max(0, innerW.value - edgePad * 2))

const startPx = computed(() => {
  const d = Math.max(0, Number(props.duration || 0))
  if (!d || !usableW.value) return edgePad
  return Math.round(edgePad + (startC.value / d) * usableW.value)
})

const endPx = computed(() => {
  const d = Math.max(0, Number(props.duration || 0))
  if (!d || !usableW.value) return edgePad
  return Math.round(edgePad + (endC.value / d) * usableW.value)
})

const seekPx = computed(() => {
  const d = Math.max(0, Number(props.duration || 0))
  if (!d || !usableW.value) return edgePad
  const t = seekC.value
  const px = Math.round(edgePad + (t / d) * usableW.value)
  return Math.max(Math.min(startPx.value, endPx.value), Math.min(Math.max(startPx.value, endPx.value), px))
})

const rangeStyle = computed(() => {
  const l = Math.min(startPx.value, endPx.value)
  const r = Math.max(startPx.value, endPx.value)
  return { left: `${l}px`, width: `${Math.max(0, r - l)}px` }
})

const maskTimeC = computed(() => {
  const v = props.mask
  if (typeof v !== 'number' || !Number.isFinite(v)) return null
  return clampTime(v)
})

const maskInTrim = computed(() => {
  const t = maskTimeC.value
  if (t === null) return false
  return t >= startC.value - 0.0001 && t <= endC.value + 0.0001
})

const maskPx = computed(() => {
  const t = maskTimeC.value
  const d = Math.max(0, Number(props.duration || 0))
  if (t === null || !d || !usableW.value) return edgePad
  return Math.round(edgePad + (t / d) * usableW.value)
})

function railScrollLeft() {
  return (railEl.value as any)?.scrollLeft ? Number((railEl.value as any).scrollLeft) : 0
}

function clientToTrackX(clientX: number) {
  const rail = railEl.value
  const track = trackEl.value
  if (!rail || !track) return 0
  const rect = rail.getBoundingClientRect()
  const x = clientX - rect.left + railScrollLeft()
  const minX = edgePad
  const maxX = edgePad + usableW.value
  return Math.max(minX, Math.min(maxX, x))
}

function xToTime(x: number) {
  const d = Math.max(0, Number(props.duration || 0))
  if (!d || !usableW.value) return 0
  return (clamp01((x - edgePad) / usableW.value) * d) || 0
}

function showTooltipAt(x: number, time: number) {
  tooltipOpen.value = true
  tooltipX.value = x
  tooltipText.value = mmss(time)
}

let previewTrimTimer = 0
let previewSeekTimer = 0
function emitPreviewTrimDebounced(time: number) {
  if (!durationOk.value) return
  const t = clampTime(time)
  if (previewTrimTimer) window.clearTimeout(previewTrimTimer)
  previewTrimTimer = window.setTimeout(() => emit('preview-trim', t), 40)
}

function emitPreviewSeekDebounced(time: number) {
  if (!durationOk.value) return
  const t = clampSeekToTrim(time)
  if (previewSeekTimer) window.clearTimeout(previewSeekTimer)
  previewSeekTimer = window.setTimeout(() => emit('preview-seek', t), 40)
}

function beginDrag(kind: 'start' | 'end' | 'range' | 'seek', e: PointerEvent) {
  if (props.disabled) return
  if (!durationOk.value) return
  dragging.value = kind
  dragPointerId.value = e.pointerId
  dragStartX.value = e.clientX
  dragStartStart.value = startC.value
  dragStartEnd.value = endC.value
  dragStartSeek.value = seekC.value
  dragSeekOffsetFromStart.value = seekC.value - startC.value
  dragSeekOffsetFromEnd.value = endC.value - seekC.value
  tooltipOpen.value = true
  if (kind === 'seek') emit('seek-dragging', true)
  try {
    ;(e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId)
  } catch {}
  if (kind === 'range') {
    const x = clientToTrackX(e.clientX)
    const left = Math.min(startPx.value, endPx.value)
    dragRangeOffset.value = x - left
  }
}

function moveDrag(e: PointerEvent) {
  if (props.disabled) return
  if (!dragging.value) return
  if (dragPointerId.value !== e.pointerId) return
  const x = clientToTrackX(e.clientX)
  const d = Math.max(0, Number(props.duration || 0))
  const gap = Math.max(0, Number(props.minGap || 0))
  if (!d || !innerW.value) return

  if (dragging.value === 'seek') {
    const t = clampSeekToTrim(xToTime(x))
    setSeek(t)
    showTooltipAt(x, t)
    emitPreviewSeekDebounced(t)
    return
  }

  if (dragging.value === 'start') {
    const t = xToTime(x)
    const nextStart = Math.min(t, endC.value - gap)
    setStartEnd(nextStart, endC.value)
    setSeek(nextStart)
    showTooltipAt(x, nextStart)
    emitPreviewTrimDebounced(nextStart)
    return
  }

  if (dragging.value === 'end') {
    const t = xToTime(x)
    const nextEnd = Math.max(t, startC.value + gap)
    setStartEnd(startC.value, nextEnd)
    setSeek(nextEnd)
    showTooltipAt(x, nextEnd)
    emitPreviewTrimDebounced(nextEnd)
    return
  }

  const rangeW = Math.abs(endPx.value - startPx.value)
  const minLeft = edgePad
  const maxLeft = Math.max(edgePad, edgePad + usableW.value - rangeW)
  const leftX = Math.max(minLeft, Math.min(maxLeft, x - dragRangeOffset.value))
  const rightX = leftX + rangeW
  const leftT = xToTime(leftX)
  const rightT = xToTime(rightX)
  if (rightT - leftT < gap) return
  let ns = leftT
  let ne = rightT
  if (ns < 0) {
    ne += -ns
    ns = 0
  }
  if (ne > d) {
    ns -= ne - d
    ne = d
  }
  ns = Math.max(0, ns)
  ne = Math.min(d, ne)
  if (ne - ns < gap) return
  setStartEnd(ns, ne)
  setSeek(ns + dragSeekOffsetFromStart.value)
  const midT = clampSeekToTrim(xToTime(x))
  showTooltipAt(x, midT)
  emitPreviewTrimDebounced(midT)
}

function endDrag(e?: PointerEvent) {
  if (e && dragPointerId.value !== e.pointerId) return
  const wasSeek = dragging.value === 'seek'
  dragging.value = null
  dragPointerId.value = null
  tooltipOpen.value = false
  if (wasSeek) emit('seek-dragging', false)
}

function onRailPointerDown(e: PointerEvent) {
  if (props.disabled) return
  if (!durationOk.value) return
  if (e.pointerType === 'mouse' && (e as any).button === 2) return
  const x = clientToTrackX(e.clientX)
  const t = xToTime(x)
  const s = startC.value
  const en = endC.value
  if (t >= s && t <= en) {
    beginDrag('seek', e)
    moveDrag(e)
    return
  }
  const mid = (s + en) / 2
  const kind = Math.abs(t - s) <= Math.abs(t - en) ? 'start' : 'end'
  beginDrag(kind, e)
  moveDrag(e)
  showTooltipAt(x, kind === 'start' ? Math.min(t, mid) : Math.max(t, mid))
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }) {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}

function onAnyPointerDown(e: PointerEvent) {
  pinchPointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
  if (pinchPointers.size === 2) {
    const pts = Array.from(pinchPointers.values())
    const a = pts[0]
    const b = pts[1]
    if (!a || !b) return
    pinchStartDist.value = dist(a, b)
    pinchStartZoom.value = zoom.value
    pinchActive.value = true
  }
}

function onAnyPointerMove(e: PointerEvent) {
  if (!pinchPointers.has(e.pointerId)) return
  pinchPointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
  if (!pinchActive.value) return
  if (pinchPointers.size !== 2) return
  const pts = Array.from(pinchPointers.values())
  const d0 = pinchStartDist.value
  const a = pts[0]
  const b = pts[1]
  if (!a || !b) return
  const d1 = dist(a, b)
  if (!d0 || !d1) return
  const ratio = d1 / d0
  const next = Math.max(zoomMin, Math.min(zoomMax, pinchStartZoom.value * ratio))
  zoom.value = Math.round(next * 100) / 100
}

function onAnyPointerUp(e: PointerEvent) {
  pinchPointers.delete(e.pointerId)
  if (pinchPointers.size < 2) pinchActive.value = false
}

function centerRailAroundRange() {
  const rail = railEl.value as any
  if (!rail) return
  const left = Math.min(startPx.value, endPx.value)
  const right = Math.max(startPx.value, endPx.value)
  const target = Math.max(0, Math.round((left + right) / 2 - rail.clientWidth / 2))
  rail.scrollLeft = target
}

function onMarkEnter() {
  const t = maskTimeC.value
  if (t === null) return
  showTooltipAt(maskPx.value, t)
}

function onMarkLeave() {
  tooltipOpen.value = false
}

function onMarkClick() {
  const t = maskTimeC.value
  if (t === null) return
  setSeek(t)
  emit('preview-seek', t)
}

watch(
  () => props.duration,
  async () => {
    await nextTick()
    if (zoom.value < 1) zoom.value = 1
    centerRailAroundRange()
    setSeek(props.seek)
  },
)

watch(
  () => zoom.value,
  () => {
    centerRailAroundRange()
  },
)

watch(
  () => [props.start, props.end, props.seek, props.duration],
  () => {
    const clamped = clampSeekToTrim(props.seek)
    if (Math.abs(clamped - Number(props.seek || 0)) > 0.001) emit('update:seek', roundMs(clamped))
  },
)

onMounted(() => {
  const el = wrapEl.value
  if (el && typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => {
      wrapW.value = Math.round(el.getBoundingClientRect().width)
    })
    ro.observe(el)
    wrapW.value = Math.round(el.getBoundingClientRect().width)
  }
  window.addEventListener('pointerdown', onAnyPointerDown, { passive: true })
  window.addEventListener('pointermove', onAnyPointerMove, { passive: true })
  window.addEventListener('pointerup', onAnyPointerUp, { passive: true })
  window.addEventListener('pointercancel', onAnyPointerUp, { passive: true })
})

onBeforeUnmount(() => {
  if (previewTrimTimer) window.clearTimeout(previewTrimTimer)
  if (previewSeekTimer) window.clearTimeout(previewSeekTimer)
  previewTrimTimer = 0
  previewSeekTimer = 0
  try {
    ro?.disconnect()
  } catch {}
  ro = null
  window.removeEventListener('pointerdown', onAnyPointerDown as any)
  window.removeEventListener('pointermove', onAnyPointerMove as any)
  window.removeEventListener('pointerup', onAnyPointerUp as any)
  window.removeEventListener('pointercancel', onAnyPointerUp as any)
})
</script>

<template>
  <div
    ref="wrapEl"
    class="trWrap"
    :class="{ trHover: hover, trDisabled: props.disabled, trNoBg: props.noBg }"
    :style="({ '--edge-pad': `${edgePad}px` } as any)"
    aria-label="时间区间选择控件"
  >
    <div
      ref="railEl"
      class="trRail"
      @mouseenter="hover = true"
      @mouseleave="hover = false"
      @pointerdown="onRailPointerDown"
      @pointermove="moveDrag"
      @pointerup="endDrag"
      @pointercancel="endDrag"
    >
      <div ref="trackEl" class="trTrack" :style="{ width: `${innerW}px` }">
        <div class="trBase"></div>
        <div class="trRange" :style="rangeStyle" @pointerdown.stop.prevent="beginDrag('range', $event as any)" @pointermove="moveDrag" @pointerup="endDrag" @pointercancel="endDrag"></div>

        <div
          class="trSeekWrap"
          :style="{ left: `${seekPx}px` }"
          aria-label="播放位置"
          @pointerdown.stop.prevent="beginDrag('seek', $event as any)"
          @pointermove="moveDrag"
          @pointerup="endDrag"
          @pointercancel="endDrag"
        >
          <div class="trSeekCap"></div>
          <div class="trSeekLine"></div>
        </div>

        <button
          class="trHandle trHandleStart"
          type="button"
          aria-label="起始时间"
          :style="{ left: `${startPx}px` }"
          @pointerdown.stop.prevent="beginDrag('start', $event as any)"
          @pointermove="moveDrag"
          @pointerup="endDrag"
          @pointercancel="endDrag"
        ></button>
        <button
          class="trHandle trHandleEnd"
          type="button"
          aria-label="结束时间"
          :style="{ left: `${endPx}px` }"
          @pointerdown.stop.prevent="beginDrag('end', $event as any)"
          @pointermove="moveDrag"
          @pointerup="endDrag"
          @pointercancel="endDrag"
        ></button>

        <button
          v-if="maskInTrim"
          class="trMark"
          type="button"
          :style="{ left: `${maskPx}px` }"
          aria-label="mask标记"
          @mouseenter="onMarkEnter"
          @mouseleave="onMarkLeave"
          @click="onMarkClick"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 2l3 7 7 3-7 3-3 7-3-7-7-3 7-3 3-7Z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="tooltipOpen" class="trTip" :style="{ left: `${tooltipX}px` }" aria-hidden="true">
      {{ tooltipText }}
    </div>
  </div>
</template>

<style scoped>
.trWrap {
  position: relative;
  width: 100%;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 0 0 2px rgba(0, 139, 255, 0.35), 0 12px 28px rgba(0, 0, 0, 0.22);
  transition: background 180ms ease, box-shadow 180ms ease;
  overflow: hidden;
  touch-action: pan-x pinch-zoom;
}

.trWrap.trNoBg {
  background: transparent;
  box-shadow: 0 0 0 2px rgba(0, 139, 255, 0.35);
}

.trWrap.trHover {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 2px rgba(0, 139, 255, 0.55), 0 12px 28px rgba(0, 0, 0, 0.22);
}

.trWrap.trDisabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.trWrap.trDisabled .trRail {
  pointer-events: none;
}

.trRail {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  touch-action: pan-x pinch-zoom;
}

.trRail::-webkit-scrollbar {
  display: none;
}

.trTrack {
  height: 44px;
  position: relative;
}

.trBase {
  position: absolute;
  left: var(--edge-pad);
  right: var(--edge-pad);
  top: 50%;
  height: 4px;
  transform: translateY(-50%);
  border-radius: 0;
  background: rgba(255, 255, 255, 0.78);
  border: none;
}

.trRange {
  position: absolute;
  top: 50%;
  height: 4px;
  transform: translateY(-50%);
  border-radius: 0;
  background: var(--accent);
  border: none;
  box-shadow: none;
  cursor: grab;
  transition: background 150ms ease, border-color 150ms ease;
}

.trRange:active {
  cursor: grabbing;
}

.trHandle {
  position: absolute;
  top: 50%;
  width: 18px;
  height: 28px;
  transform: translate(-50%, -50%);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.25);
  transition: transform 120ms ease, background 150ms ease, border-color 150ms ease;
  cursor: ew-resize;
}

.trHandle:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px rgba(0, 139, 255, 0.35);
}

.trHandle:hover {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.22);
}

.trSeekWrap {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 22px;
  height: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  cursor: ew-resize;
  z-index: 4;
}

.trSeekWrap::before {
  content: '';
  position: absolute;
  inset: -12px;
}

.trSeekCap {
  width: 14px;
  height: 6px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.18);
}

.trSeekLine {
  width: 2px;
  flex: 1 1 auto;
  margin: 2px 0 0;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.16);
}

.trMark {
  position: absolute;
  top: 50%;
  width: 22px;
  height: 22px;
  transform: translate(-50%, -50%);
  border-radius: 999px;
  border: 2px solid rgba(0, 139, 255, 0.9);
  background: rgba(0, 139, 255, 0.18);
  color: rgba(0, 139, 255, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.22);
  cursor: pointer;
}

.trMark:hover {
  filter: brightness(1.05);
}

.trTip {
  position: absolute;
  top: -10px;
  transform: translate(-50%, -100%);
  padding: 6px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.92);
  font-size: 12px;
  font-weight: 800;
  pointer-events: none;
  white-space: nowrap;
  backdrop-filter: blur(10px);
}
</style>
